import { useEffect, useState, useRef } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ModelosAgrupados } from "@/components/ModelosAgrupados";
import { AnchoredList } from "@/components/AnchoredList";

const CAMPOS_VACIOS = { nombre: "", categoria: "", color: "", nombres_alternativos: "", grupo_equivalencia_id: null, modelo_ids: [] };
const APLICAR_VACIO = { nombre: false, categoria: false, color: false, nombres_alternativos: false, grupo_equivalencia_id: false, modelo_ids: false };

// Encabezado con check "aplicar este campo": si no está marcado, el campo no se toca.
const CampoAplicable = ({ id, label, aplicar, onToggle, children }) => (
  <div className={`space-y-1 rounded-sm border p-2.5 transition-colors ${aplicar ? "border-indigo-300 bg-indigo-50/40" : "border-border"}`}>
    <label className="flex items-center gap-2 cursor-pointer">
      <Checkbox data-testid={`bulk-aplicar-${id}`} checked={aplicar} onCheckedChange={onToggle} />
      <span className="text-sm font-medium">{label}</span>
      {!aplicar && <span className="text-[10px] text-muted-foreground uppercase font-semibold">sin cambios</span>}
    </label>
    <div className={aplicar ? "" : "opacity-50 pointer-events-none"}>{children}</div>
  </div>
);

export const RepuestoBulkEditDialog = ({ open, ids, onOpenChange, onSaved }) => {
  const n = ids?.length || 0;
  const [form, setForm] = useState(CAMPOS_VACIOS);
  const [aplicar, setAplicar] = useState(APLICAR_VACIO);
  const [autoModelos, setAutoModelos] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [compatibles, setCompatibles] = useState([]);
  const [qComp, setQComp] = useState("");
  const [resComp, setResComp] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const compRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(CAMPOS_VACIOS); setAplicar(APLICAR_VACIO); setAutoModelos(false);
    setCompatibles([]); setQComp(""); setResComp([]);
    api.get("/grupos-equivalencia").then((r) => setGrupos(r.data)).catch(() => {});
    api.get("/modelos").then((r) => setModelos(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    const term = qComp.trim();
    if (!term) { setResComp([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/repuestos", { params: { q: term, limit: 8 } });
        setResComp(data.filter((r) => !compatibles.some((c) => c.id === r.id)));
      } catch { setResComp([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [qComp, compatibles]);

  const marcar = (campo, valor) => { setForm((f) => ({ ...f, [campo]: valor })); setAplicar((a) => ({ ...a, [campo]: true })); };
  const toggleAplicar = (campo) => setAplicar((a) => ({ ...a, [campo]: !a[campo] }));

  const guardar = async () => {
    const aplicarKeys = Object.keys(aplicar).filter((k) => aplicar[k]);
    if (aplicarKeys.length === 0 && !autoModelos && compatibles.length === 0) {
      toast.error("No modificaste ningún campo"); return;
    }
    if (!window.confirm(`Se actualizarán ${n} repuestos. ¿Continuar?`)) return;
    const payload = {
      ids, aplicar: aplicarKeys, autocompletar_modelos: autoModelos,
      nombre: form.nombre, categoria: form.categoria, color: form.color,
      nombres_alternativos: form.nombres_alternativos,
      grupo_equivalencia_id: form.grupo_equivalencia_id || null,
      modelo_ids: form.modelo_ids,
      compatibles_agregar: compatibles.map((c) => c.id),
    };
    setGuardando(true);
    try {
      const { data } = await api.patch("/repuestos/bulk", payload);
      toast.success(`Se actualizaron ${data.actualizados} repuestos`);
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setGuardando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-edit-dialog" className="bg-white max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Editar {n} repuestos</DialogTitle>
          <p className="text-xs text-muted-foreground">Solo se aplican los campos que actives. Los demás conservan su valor actual en cada repuesto.</p>
        </DialogHeader>
        <div className="space-y-3 flex-1 overflow-y-auto pr-1 -mr-1">
          <CampoAplicable id="nombre" label="Nombre" aplicar={aplicar.nombre} onToggle={() => toggleAplicar("nombre")}>
            <Input data-testid="bulk-nombre-input" value={form.nombre} onChange={(e) => marcar("nombre", e.target.value)} />
          </CampoAplicable>
          <CampoAplicable id="categoria" label="Categoría" aplicar={aplicar.categoria} onToggle={() => toggleAplicar("categoria")}>
            <Input data-testid="bulk-categoria-input" value={form.categoria} placeholder="Ej: frenos, motor..."
              onChange={(e) => marcar("categoria", e.target.value)} />
          </CampoAplicable>
          <CampoAplicable id="grupo_equivalencia_id" label="Grupo de equivalencia" aplicar={aplicar.grupo_equivalencia_id} onToggle={() => toggleAplicar("grupo_equivalencia_id")}>
            <Select value={form.grupo_equivalencia_id || "ninguno"}
              onValueChange={(v) => marcar("grupo_equivalencia_id", v === "ninguno" ? null : v)}>
              <SelectTrigger data-testid="bulk-grupo-eq-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ninguno">Sin grupo</SelectItem>
                {grupos.map((g) => <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </CampoAplicable>
          <CampoAplicable id="nombres_alternativos" label="Nombre alternativo (alias)" aplicar={aplicar.nombres_alternativos} onToggle={() => toggleAplicar("nombres_alternativos")}>
            <Input data-testid="bulk-alt-input" value={form.nombres_alternativos} placeholder="Ej: oring, sello de varilla"
              onChange={(e) => marcar("nombres_alternativos", e.target.value)} />
          </CampoAplicable>
          <CampoAplicable id="color" label="Código de color" aplicar={aplicar.color} onToggle={() => toggleAplicar("color")}>
            <Input data-testid="bulk-color-input" value={form.color} placeholder="Ej: NH196, R264C"
              onChange={(e) => marcar("color", e.target.value)} />
          </CampoAplicable>

          <div className="space-y-1 rounded-sm border border-border p-2.5">
            <Label>Repuestos compatibles (se agregan a todos)</Label>
            <div className="relative" ref={compRef}>
              <Input data-testid="bulk-compatibles-input" value={qComp} placeholder="Buscar por nombre o código de parte..."
                onChange={(e) => setQComp(e.target.value)} />
              <AnchoredList anchorRef={compRef} open={resComp.length > 0}>
                {resComp.map((r) => (
                  <button key={r.id} type="button" data-testid={`bulk-compatible-opcion-${r.id}`}
                    onClick={() => { setCompatibles((c) => [...c, { id: r.id, codigo_parte: r.codigo_parte, nombre: r.nombre }]); setQComp(""); setResComp([]); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 border-b border-slate-50 last:border-0">
                    <span className="font-mono text-xs text-slate-500 shrink-0">{r.codigo_parte}</span>
                    <span className="truncate">{r.nombre}</span>
                  </button>
                ))}
              </AnchoredList>
            </div>
            {compatibles.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {compatibles.map((c) => (
                  <span key={c.id} data-testid={`bulk-compatible-chip-${c.id}`}
                    className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-0.5 text-xs">
                    <span className="font-mono">{c.codigo_parte}</span> {c.nombre}
                    <button type="button" title="Quitar" onClick={() => setCompatibles((prev) => prev.filter((x) => x.id !== c.id))}
                      className="text-amber-500 hover:text-amber-800">×</button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">La relación es mutua y no se duplica. Los campos que dejes vacíos aquí no se tocan.</p>
          </div>

          <div className="space-y-2 rounded-sm border border-border p-2.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox data-testid="bulk-autocompletar-modelos" checked={autoModelos} onCheckedChange={(v) => setAutoModelos(!!v)} />
              <span className="text-sm font-medium">Autocompletar modelos compatibles desde catálogos de referencia</span>
            </label>
            <p className="text-xs text-muted-foreground">Al guardar, a cada repuesto se le agregan los modelos donde aparece su propio código (unión con los que ya tenía).</p>
            <CampoAplicable id="modelo_ids" label="…o fijar modelos compatibles (mismos para todos)" aplicar={aplicar.modelo_ids} onToggle={() => toggleAplicar("modelo_ids")}>
              <ModelosAgrupados modelos={modelos} seleccionados={form.modelo_ids} testPrefix="bulk-modelo"
                onChange={(idsSel) => setForm((f) => ({ ...f, modelo_ids: idsSel }))}
                emptyMsg="No hay modelos registrados." />
            </CampoAplicable>
          </div>
        </div>
        <DialogFooter className="border-t border-slate-100 pt-3 shrink-0 sm:justify-between items-center">
          <span className="text-xs text-muted-foreground" data-testid="bulk-conteo">Se actualizarán {n} repuestos</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button data-testid="bulk-guardar-btn" onClick={guardar} disabled={guardando || n === 0}>
              {guardando ? "Guardando…" : `Guardar (${n})`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
